import { CalOptions, HebrewCalendar, Location } from '@hebcal/core';

import { ClientWalkOrderPossibleDays } from '../Interfaces/reports';
import { ResponseFromExpress } from '../Interfaces/SQL-interfaces';
import { Time } from './constants';
import { translateDate } from './date_functions/SQL_ISO_adapter';

export type HebrewHolidays = 'Rosh Hashana' | 'Pesach';

export function normalizeNumber(input: unknown, options: { max?: number; min?: number; lower?: boolean; upper?: boolean } = {}): number {
	let morphedInput = Number(input);

	const max = options.max ?? 100;
	const min = options.min ?? 0;
	const lower = options.lower || true;
	const upper = options.upper || true;

	if (lower) {
		morphedInput = morphNumberToHardLimit(morphedInput, { mode: 'lower', max, min });
	}

	if (upper) {
		morphedInput = morphNumberToHardLimit(morphedInput, { mode: 'upper', max, min });
	}

	return isNaN(morphedInput) ? 0 : morphedInput;
}

export function normalizeNumberToFormattedString(input: number, options: { maximumFractionDigits?: number; minimumFractionDigits?: number; useGrouping?: boolean } = {}) {
	const maximumFractionDigits = options.maximumFractionDigits ?? 2;
	const minimumFractionDigits = options.minimumFractionDigits ?? 0;
	const useGrouping = options.useGrouping ?? false;
	return Intl.NumberFormat('en-IL', { maximumFractionDigits, minimumFractionDigits, useGrouping }).format(+input);
}

export function normalizeNumberToMaxTwoDecimal(input: number | string): number {
	return +normalizeNumberToFormattedString(+input, { useGrouping: false }) || 0;
}
export function normalizeNumberToMaxThreeDecimal(input: number | string): number {
	return +normalizeNumberToFormattedString(+input, { useGrouping: false, maximumFractionDigits: 3 }) || 0;
}

function morphNumberToHardLimit(input: number, options: { mode: 'upper' | 'lower' | 'both'; min: number; max: number }): number {
	const { mode, min, max } = options;

	const morph = morphNumberToHardLimit;

	switch (mode) {
		case 'upper':
			return Math.min(input, max);
		case 'lower':
			return Math.max(input, min);
		case 'both':
			return morph(morph(input, { mode: 'upper', min, max }), { mode: 'lower', min, max });

		default:
			return input || 0;
	}
}

export function shouldDisplayDifferentLogo(holiday: HebrewHolidays) {
	const currentMonth = new Date().getMonth();

	switch (holiday) {
		case 'Rosh Hashana':
			if (currentMonth < 7 || currentMonth > 9) return false;
			break;
		case 'Pesach':
			if (currentMonth < 2 || currentMonth > 4) return false;
			break;

		default:
			break;
	}

	const lastMonth = new Date();
	lastMonth.setMonth(new Date().getMonth() - 1);
	const nextMonth = new Date();
	nextMonth.setMonth(new Date().getMonth() + 1);

	const options: CalOptions = {
		year: new Date().getFullYear(),
		location: Location.lookup('Tel Aviv'),
		il: true,
		start: lastMonth,
		end: nextMonth,
		noRoshChodesh: true,
	};

	return checkIfHoliday(options, holiday);
}

function checkIfHoliday(options: CalOptions, searchFor: HebrewHolidays) {
	const events = HebrewCalendar.calendar(options);

	const ignoreValues: string[] = ['Behemot', 'Pesach Sheni'];

	const addedValues = [];

	switch (searchFor) {
		case 'Rosh Hashana':
			addedValues.push('Sukkot');
	}

	const { week } = Time;

	for (const ev of events) {
		const hd = ev.getDate();
		const holidayDate = hd.greg();
		const holiday = ev.render('en');

		if ((holiday.includes(searchFor) || addedValues.some(add => holiday.includes(add))) && !ignoreValues.some(ignore => holiday.includes(ignore))) {
			const now = new Date().getTime();
			const holidayTime = holidayDate.getTime();
			const timeDifference = Math.abs(now - holidayTime);

			if (timeDifference < week) {
				return true;
			}
		}
	}

	return false;
}

export function arithmeticWithFloatingNumber(options: { number: number; action: 'increment' | 'decrement'; precision?: number }) {
	const precision = options?.precision || 3;
	const { action, number } = options;

	if (isNaN(number) || !isFinite(number)) {
		throw new Error('Input is not a valid number.');
	}

	const amount = Math.pow(10, precision);

	if (action === 'decrement') {
		return (number * amount - amount) / amount;
	}

	return (number * amount + amount) / amount;
}

export function formatNumberToMaxPrecision(input: number, maxPrecision = 3) {
	return Intl.NumberFormat('en-IL', { maximumFractionDigits: maxPrecision, minimumFractionDigits: 0, useGrouping: false }).format(+input);
}

/**
 * @template T
 * @param {string} input - The text to search.
 * @param {T[]} arrayToSearch - The array to search in.
 * @param {(keyof T)[]} valuesToSearch - The keys of the array to search in.
 * @returns {T[]} - The filtered array.
 * @description This function is used to filter an array of objects based on the input string. It checks if the input string matches (or is a substring of) any of the values of the specified keys in the objects. The input string can be in any order and it doesn't have to match the order of the object's values. The function is case-insensitive and it can handle any type `T` that extends `Record<any, {truthy value}>`.
 * @example
 * // The function will return an array of objects where the 'name' or 'description' contains 'apple' and 'red' in any order.
 * genericSearchingFunction('red apple',
 * [
 * {name: 'Red Apple', description:'A tasty fruit'},
 * {name: 'Green Apple', description: 'A sour fruit'},
 * {name: 'Red Shiny PrApple', description: 'A shiny red fruit'},
 * {name: 'Banana', description: 'A yellow fruit'}
 * ], ['name', 'description'])
 * // => [{name: 'Red Apple', description:'A tasty fruit'}, {name:'Red Shiny PrApple', description: 'A shiny red fruit'}]
 */
export function genericSearchingFunction<T extends Record<K, string | number | Date>, K extends keyof T>(input: string, arrayToSearch: T[], valuesToSearch: K[], sliceLength = 50): T[] {
	if (!arrayToSearch.length) return arrayToSearch;
	const inputLength = input.length || 0;

	const finalArray = [];

	if (!inputLength) {
		return arrayToSearch.slice(0, sliceLength);
	}

	try {
		valuesToSearch.map(valueToSearch => arrayToSearch[0][valueToSearch].toString());
	} catch (error) {
		console.error('values must have toString method or be a string');
		return arrayToSearch.slice(0, sliceLength);
	}

	const splitValue = input.split(' ');

	for (let i = 0; i < arrayToSearch.length; i++) {
		let counter = 0;

		const mappedValues = valuesToSearch.map(valueToSearch => (arrayToSearch[i][valueToSearch] || '').toString().replace(/\s/g, '').toLowerCase());
		const allStrings = mappedValues.every(value => typeof value === 'string');

		if (!allStrings) continue;

		for (let j = 0; j < splitValue.length; j++) {
			if (mappedValues.some(value => value.indexOf(splitValue[j]) > -1)) {
				counter++;
			}
		}

		if (counter >= splitValue.length) {
			finalArray.push(arrayToSearch[i]);
		}
	}

	return finalArray.slice(0, sliceLength);
}
/**
 * @template T
 * @param {string} input - The text to search.
 * @param {T[]} arrayToSearch - The array to search in.
 * @param {(keyof T)[]} valuesToSearch - The keys of the array to search in.
 * @returns {T[]} - The filtered array.
 * @description This function is used to filter an array of objects based on the input string. It checks if the input string matches (or is a substring of) any of the values of the specified keys in the objects. The input string can be in any order and it doesn't have to match the order of the object's values. The function is case-insensitive and it can handle any type `T` that extends `Record<any, {truthy value}>`.
 * @example
 * // The function will return an array of objects where the 'name' or 'description' contains 'apple' and 'red' in any order.
 * genericSearchingFunction('red apple',
 * [
 * {name: 'Red Apple', description:'A tasty fruit'},
 * {name: 'Green Apple', description: 'A sour fruit'},
 * {name: 'Red Shiny PrApple', description: 'A shiny red fruit'},
 * {name: 'Banana', description: 'A yellow fruit'}
 * ], ['name', 'description'])
 * // => [{name: 'Red Apple', description:'A tasty fruit'}, {name:'Red Shiny PrApple', description: 'A shiny red fruit'}]
 */
export function genericSearchingFunctionWithoutNullChecks<T, K extends keyof T>(input: string, arrayToSearch: T[], valuesToSearch: K[], sliceLength = 50): T[] {
	if (!arrayToSearch?.length) return arrayToSearch;
	const inputLength = input.length || 0;

	const finalArray = [];

	if (!inputLength) {
		return arrayToSearch.slice(0, sliceLength);
	}

	const splitValue = input.toLowerCase().split(' ');

	for (let i = 0; i < arrayToSearch.length; i++) {
		let counter = 0;

		const mappedValues = valuesToSearch.map(valueToSearch => (arrayToSearch[i][valueToSearch] || '').toString().replace(/\s/g, '').toLowerCase());
		const allStrings = mappedValues.every(value => typeof value === 'string');

		if (!allStrings) continue;

		for (let j = 0; j < splitValue.length; j++) {
			if (mappedValues.some(value => value.indexOf(splitValue[j]) > -1)) {
				counter++;
			}
		}

		if (counter >= splitValue.length) {
			finalArray.push(arrayToSearch[i]);
		}
	}

	return finalArray.slice(0, sliceLength);
}

export function genericSimpleFilterFunction<T>(value: string | number, array: T[], key: keyof T extends string ? keyof T : never): T[] {
	const filterValue = String(value).toLowerCase();

	return array.filter(item => String(item[key]).toLowerCase().includes(filterValue));
}

export function getOnlyDistinctValues<T, K extends keyof T>(array: T[], key: K): T[K][] {
	return Array.from(new Set(array.map(item => item[key])));
}

export function createEmptyMsSQLResponse<T = void>(response: T[] = []): ResponseFromExpress<T> {
	return {
		output: {},
		recordsets: [response],
		recordset: response,
		rowsAffected: [response.length],
	};
}

export function formatDecimalStringOrNumberToMaxThree(input: unknown): number {
	input ||= 0;
	const number = +input;
	if (!isFinite(number)) {
		return 0;
	}
	return +number.toLocaleString('en-US', { maximumFractionDigits: 3, useGrouping: false });
}

export function dayToHebrewDay(day: ClientWalkOrderPossibleDays): string {
	switch (day) {
		case 'Sunday':
			return 'ראשון';
		case 'Monday':
			return 'שני';
		case 'Tuesday':
			return 'שלישי';
		case 'Wednesday':
			return 'רביעי';
		case 'Thursday':
			return 'חמישי';
		case 'Friday':
			return 'שישי';
		case 'Saturday':
			return 'שבת';
	}
}

export function getDateAsID() {
	return translateDate(new Date(), 'toSQL')
		.toISOString()
		.slice(2, 19)
		.split(/[-: T]/)
		.join('');
}

// TODO refactor this function. It doesn't seem to do anything useful?
// current implementation simply returns the input surrounded by the key sent?
export function groupByKeyOfData<T, K extends keyof T>(input: { data: T[]; keyToGroupBy: K; asRecord?: false }): T[];
export function groupByKeyOfData<T, K extends keyof T>(input: { data: T[]; keyToGroupBy: K; asRecord: true }): Record<K, T[]>;
export function groupByKeyOfData<T, K extends keyof T>(input: { data: T[]; keyToGroupBy: K; asRecord?: boolean }): Record<K, T[]> | T[] {
	const { data, keyToGroupBy, asRecord } = input;

	const record = data.reduce((prev, curr) => {
		prev[keyToGroupBy] ||= [];
		prev[keyToGroupBy].push(curr);
		return prev;
	}, {} as Record<K, T[]>);

	return asRecord ? record : Object.values(record);
}

export function sumOfKeyInData<T, K extends keyof T>(input: { data: T[]; keyToSum: K }): number {
	const { data, keyToSum } = input;

	return data.reduce((prev, curr) => prev + +curr[keyToSum], 0);
}

export function range(stop: number): number[];
export function range(start?: number, stop?: number, step?: number): number[];
export function range(start?: number, stop?: number, step = 1) {
	if (stop === undefined) {
		stop = start || 0;
		start = 0;
	}

	return Array.from({ length: Math.abs(Math.ceil((stop - start) / step)) }, (_, i) => start + i * step);
}
